import React, { useEffect, useState } from 'react'
import api from 'config/axiosConfig'
import { useParams, useLocation, useSearchParams, useNavigate } from 'react-router-dom'
import IssueCard from '../IssueCard'
import IssueProfileTabs from './IssueProfileTabs'
import { setDocumentTitle, setEntityOg } from 'util/ogMetadataUtils'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'
import { addRecentIssue } from 'util/recentIssuesUtils'
import { Issue } from 'util/constants'
import { apiRoutes } from "util/apiRoutes";

const IssueProfile = () => {
    const { cname } = useParams<{ cname: string }>()
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [issue, setIssue] = useState<Issue | null>(null)
    const [error, setError] = useState('')

    useEffect(() => {
        setDocumentTitle(`Issue - ${cname?.replace(/_/g, ' ')}`)
    }, [cname])

    useEffect(() => {
        const fetchIssue = async () => {
            try {
                // First try to get the issue by name
                try {
                    if (!cname) {
                        throw new Error('Missing issue cname')
                    }
                    // Encode cname to safely handle names containing '/'
                    const response = await api.get(apiRoutes.issues.byName(encodeURIComponent(cname)))
                    const issueData = response.data
                    const params = new URLSearchParams(location.search)
                    if (params.get('upvoted') === 'true') {
                        issueData.user_vote = 'user_upvote'
                    }
                    setIssue(issueData)
                    // Update OG/Twitter metadata for this issue
                    setEntityOg({
                        entityType: 'issue',
                        entity: issueData,
                        urlPath: `/issue/${encodeURIComponent(issueData.cname)}`,
                    })
                    return
                } catch (nameError) {
                    // If name lookup fails, try id lookup
                    if (!isNaN(Number(cname))) {
                        try {
                            const idResponse = await api.get(apiRoutes.issues.byId(cname as string))
                            const issueData = idResponse.data
                            
                            // Construct the new URL preserving additional path segments
                            // Encode cname path segment to avoid '/' splitting
                            let newPath = `/issue/${encodeURIComponent(issueData.cname)}`;
                            
                            // Preserve additional path segments from the current URL
                            const pathSegments = location.pathname.split('/');
                            if (pathSegments.length > 3) {
                                // Get all segments after /issue/:id
                                const additionalSegments = pathSegments.slice(3).join('/');
                                newPath = `${newPath}/${additionalSegments}`;
                            }
                            
                            // Redirect to the canonical URL using cname while preserving path segments
                            navigate(`${newPath}${location.search}`, { replace: true })
                            return
                        } catch (idError) {
                            // If both lookups fail, throw error to be caught by outer catch
                            throw idError
                        }
                    } else {
                        // If cname isn't a number, throw the original error
                        throw nameError
                    }
                }
            } catch (err) {
                if (err instanceof AxiosError && err.response?.status === 404) {
                    setError('404: Failed to load issue profile. Please try again later.')
                } else {
                    setError('Failed to load issue profile. Please try again later.')
                }
            }
        }

        fetchIssue()
    }, [cname, location.search, searchParams, navigate, location.pathname])

    // Track recent issue visit
    useEffect(() => {
        if (issue) {
            try {
                addRecentIssue(issue);
            } catch (error) {
                console.error('Error adding recent issue:', error);
            }
        }
    }, [issue]);

    if (error) {
        return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find this issue.`} buttonText={`View Other Issues`} buttonHref={`/issues/new`} /> : <ServerErrorPage />
    }

    if (!issue) {
        return (
            <LoadingSkeleton count={1} variant="issue-card" margin="mt-4" />
        )
    }

    return (
        <div className="py-0 relative">
            <div>
                <IssueCard
                    issue={issue}
                    showMeta={true}
                    truncateDescription={true}
                    isIssueProfilePage={true}
                />
            </div>
            <IssueProfileTabs issue={issue} />
        </div>
    )
}

export default IssueProfile
